import Dexie, { type Table } from 'dexie';
import type { Word, Enrichment, UserWordState } from '../types';

export interface SyncAction {
  id?: number;
  type: 'review' | 'quiz' | 'progress';
  payload: Record<string, unknown>;
  created_at: number;
  retries: number;
}

export class GREDatabase extends Dexie {
  words!: Table<Word, string>;
  enrichmentCache!: Table<{ word_id: string; data: Enrichment; fetched_at: number }, string>;
  userStates!: Table<UserWordState, string>;
  syncQueue!: Table<SyncAction, number>;

  constructor() {
    super('GREVocabStudio');
    this.version(1).stores({
      words: 'id, word, difficulty, *tags',
      enrichmentCache: 'word_id, fetched_at',
      userStates: 'word_id, status, next_review',
      syncQueue: '++id, type, created_at',
    });
  }
}

export const db = new GREDatabase();

export async function cacheWords(words: Word[]): Promise<void> {
  await db.words.bulkPut(words);
}

export async function getCachedWords(): Promise<Word[]> {
  return db.words.toArray();
}

export async function cacheEnrichment(wordId: string, data: Enrichment): Promise<void> {
  await db.enrichmentCache.put({ word_id: wordId, data, fetched_at: Date.now() });
}

export async function getCachedEnrichment(wordId: string): Promise<Enrichment | undefined> {
  const entry = await db.enrichmentCache.get(wordId);
  return entry?.data;
}

export async function cacheUserState(state: UserWordState): Promise<void> {
  await db.userStates.put(state);
}

export async function getCachedUserStates(): Promise<UserWordState[]> {
  return db.userStates.toArray();
}

export async function queueSyncAction(action: Omit<SyncAction, 'id'>): Promise<void> {
  await db.syncQueue.add({ ...action, retries: 0 });
}

export async function getSyncQueue(): Promise<SyncAction[]> {
  return db.syncQueue.orderBy('created_at').toArray();
}

export async function removeSyncAction(id: number): Promise<void> {
  await db.syncQueue.delete(id);
}

export async function incrementRetry(id: number): Promise<void> {
  const action = await db.syncQueue.get(id);
  if (action) {
    await db.syncQueue.update(id, { retries: action.retries + 1 });
  }
}
